import React from 'react';
import { linkPagePath, matchPath, normaliseRedirects } from './match-path';
import { conditionalStateAttributes, omittedByRules, readStateWhen, type ConditionContext, type VisitorAudience } from './conditions';
import type { PageLiveData } from './page-runtime';

/**
 * What the host that renders a builder tree tells the elements inside it
 * (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13). One context so every element
 * asks the same question the same way, on the server and in the browser.
 *
 * - `linksLive`: links carry their real `href`. A page route, a preview
 *   iframe and the static HTML render say yes; an element rendered inside the
 *   editor's own document (no provider) falls back to the old path test,
 *   because there a real `href` would navigate the editor away.
 * - `currentPath`: the path of the page being shown, for `aria-current`.
 *   Header and footer trees sit inside the same provider, so their links
 *   match the page they are shown on.
 */
export interface RenderHost {
  linksLive?: boolean;
  currentPath?: string;
  /**
   * What live states need, from the page loader (live-data.server.ts). `now`
   * is the loader's clock, so a hydrating React render produces the same
   * attributes as the server HTML; the page runtime moves them on from there.
   * Never the visitor's own store: pages are cached and shared.
   */
  live?: PageLiveData & { now?: number };
  /**
   * Who the page is rendered for (conditions.ts, audiences): the signed-in
   * visitor's login, club, tier and segments, or absent for a guest. Only a
   * render made for one visitor sets it — a cached, shared page never does.
   */
  visitor?: VisitorAudience | null;
}

export const RenderHostContext = React.createContext<RenderHost>({});

/** The editor's own routes, where a live `href` would leave the editor. */
function insideEditorDocument(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return path.includes('/bud-builder') || path.includes('/website/');
}

/**
 * Whether links render their real `href` here. Without a provider this is the
 * old test (browser only, and not on an editor route), so nothing that renders
 * outside a host changes; with one, the host decides — the same answer on the
 * server and in the browser, so server HTML carries the `href` too.
 */
export function useLinksLive(): boolean {
  const host = React.useContext(RenderHostContext);
  if (host.linksLive !== undefined) return host.linksLive;
  return typeof window !== 'undefined' && !insideEditorDocument();
}

/** A page's path from its stored slug: `about` → `/about`, `` → `/`. */
export function pathForSlug(slug: string | undefined | null): string | undefined {
  if (slug == null) return undefined;
  return '/' + String(slug).replace(/^\/+|\/+$/g, '');
}

export function useRenderHost(): RenderHost {
  return React.useContext(RenderHostContext);
}

const redirectCache = new WeakMap<object, Record<string, string>>();

/**
 * `aria-current` for a link to `href`, plus the `data-pb-current` marker the
 * page runtime uses on plain-HTML sites. Nothing at all when the host does
 * not know the current path (the editor) or the link is not to a page here.
 */
export function currentLinkAttrs(
  host: RenderHost,
  href: string | undefined | null,
  exactOnly = false,
): Record<string, string> {
  if (!href || host.currentPath === undefined) return {};
  // Only a link to a page on this site is marked: never `#`, `mailto:`,
  // another site — the marker is also the runtime's in-app navigation scope.
  if (linkPagePath(href, host.live?.siteHosts) === null) return {};
  let redirects: Record<string, string> | undefined;
  if (host.live?.redirects) {
    redirects = redirectCache.get(host.live.redirects);
    if (!redirects) {
      redirects = normaliseRedirects(host.live.redirects);
      redirectCache.set(host.live.redirects, redirects);
    }
  }
  const match = matchPath(href, host.currentPath, { exactOnly, siteHosts: host.live?.siteHosts, redirects });
  const attrs: Record<string, string> = { 'data-pb-current': exactOnly ? 'exact' : 'section' };
  if (match) attrs['aria-current'] = match;
  return attrs;
}

/**
 * The state attributes an element's `stateWhen` gives it in server HTML
 * (`aria-disabled`, `data-pb-state`, `data-pb-when`). `field` reads the
 * element's data (page / post fields, a card's item).
 */
export function conditionalAttrsFor(
  host: RenderHost,
  stateWhen: unknown,
  field?: (key: string) => unknown,
): Record<string, string> {
  const rules = readStateWhen(stateWhen);
  if (rules.length === 0) return {};
  return conditionalStateAttributes(rules, conditionContextFor(host, field));
}

/** The context every condition on this host is decided in. */
function conditionContextFor(host: RenderHost, field?: (key: string) => unknown): ConditionContext {
  const stores = host.live?.stores ?? [];
  return {
    now: host.live?.now ?? Date.now(),
    // Only a one-store site's store is known on the server (cached pages).
    store: stores.length === 1 ? stores[0] : null,
    timezone: host.live?.timezone,
    field,
    visitor: host.visitor ?? null,
  };
}

/**
 * True when the element's `visible` rule is decided against it with nothing
 * live to re-check: the renderers then leave it out of the HTML altogether
 * (conditions.ts `omittedByRules`). Never on the editing canvas, which marks
 * instead of hiding.
 */
export function omittedFor(host: RenderHost, stateWhen: unknown, field?: (key: string) => unknown): boolean {
  const rules = readStateWhen(stateWhen);
  if (rules.length === 0) return false;
  return omittedByRules(rules, conditionContextFor(host, field));
}

/** A popup / drawer trigger announces the dialog it opens (§13.2). */
export function dialogTriggerAttrs(dataAttrs: Record<string, string> | undefined): Record<string, string> {
  if (!dataAttrs) return {};
  const opens = 'data-modal-trigger' in dataAttrs || 'data-drawer-trigger' in dataAttrs || 'data-pb-site-modal' in dataAttrs;
  return opens ? { 'aria-haspopup': 'dialog', 'aria-expanded': 'false' } : {};
}

